import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule, DoBootstrap } from "@angular/core";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from "@angular/material-moment-adapter";
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { CustomMatPaginatorIntl } from "./modules/main/table/modules/custom-paginator-intl"; // Adjust path as needed

import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "saturn-datepicker";
import { ScrollPanelModule } from "primeng/scrollpanel"; // TODO: can we replace this with angular material so that we don't have to import the whole primeng library
import { map } from "rxjs/operators";
import { KeycloakAngularModule, KeycloakService } from "keycloak-angular";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routes";
import { AppMenuComponent, AppSubMenuComponent } from "./app.menu.component";
import { AppTopBarComponent } from "./app.topbar.component";
import { AppFooterComponent } from "./app.footer.component";
import { CardService } from "./card/card.service";
import { AdminService } from "./admin/admin.service";
import { FilterPipe } from "./admin/filter.pipe";
import { cardfilter } from "./card/cardfilter.pipe";
import { AppResolve } from "./appResolve.service";
import { CommonService } from "./shared/common.service";
import { UserData } from "./shared/service/UserService";
import { ContentManagementService } from "./content-management/content-management.service";
import { TableService } from "./table/table.service";
import { ConfigModule, ConfigService } from "./config/index";
import { MinuteSecondsPipe } from "./content-management/minuteSeconds.filter";
import { ProgressBarColor } from "../app/content-management/directive/progress-bar-color";
import { HelperService } from "./modules/shared/services/helper.service";
import { UserDetailsModule } from "./modules/main/user-details/user-details.module";
import { GoogleAnalyticsService } from "./google-analytics.service";
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive"; // SG-6756
import { NgIdleModule } from "@ng-idle/core"; // SG-6756
import { CommonAPIService } from "./modules/shared/services/common-api.service";
import { AtcControllerService } from "./modules/shared/services/atc-controller.service";
import { ViewReferenceDialogModule } from "./modules/main/content-management/modules/practice-set-detail/view-reference-dialog/view-reference-dialog.module";
import { HighchartsChartModule } from "highcharts-angular";
import { ConfigurationComponent } from "./configuration/configuration.component";
import { MatButtonModule } from "@angular/material/button";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { CommonTabsComponent } from "./shared/components/common-tabs/common-tabs.component";
import { DecryptInterceptor } from "./handler/DecryptInterceptor";
import { EncryptInterceptor } from "./handler/EncryptInterceptor";
import { GameTypeService } from "./core/services/game-type.service";
import { HttpInterceptorService } from "./modules/shared/services/http-interceptor.service";

let keycloakService: KeycloakService = new KeycloakService();

@NgModule({
  declarations: [AppComponent, AppMenuComponent, AppSubMenuComponent, AppTopBarComponent, AppFooterComponent, FilterPipe, cardfilter, MinuteSecondsPipe, ProgressBarColor],
  imports: [
    HighchartsChartModule,
    BrowserModule,
    UserDetailsModule,
    HttpClientModule,
    KeycloakAngularModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    // ToasterModule,
    BrowserAnimationsModule,
    ConfigModule,
    CommonModule,
    ScrollPanelModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatDialogModule,
    NgIdleModule.forRoot(), // SG-6756
    NgIdleKeepaliveModule.forRoot(),
    ViewReferenceDialogModule, // SG-6756
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatInputModule,
    FormsModule,
    CommonTabsComponent,
  ],
  providers: [
    CardService,
    AdminService,
    AppResolve,
    CommonService,
    ContentManagementService,
    TableService,
    ConfigService,
    CommonAPIService,
    AtcControllerService,

    {
      provide: "googleAnalyticsTrackingId",
      useFactory: (configService: ConfigService) => {
        return configService.getConfig().pipe(map((config: any) => config.googleAnalytics.trackingId));
      },
      deps: [ConfigService],
    },
    {
      provide: "googleAnalyticsSendPageView",
      useFactory: (configService: ConfigService) => {
        return configService.getConfig().pipe(map((config: any) => config.googleAnalytics.sendPageView));
      },
      deps: [ConfigService],
    },
    HelperService,
    GoogleAnalyticsService,
    {
      provide: KeycloakService,
      useValue: keycloakService,
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EncryptInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DecryptInterceptor, multi: true },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    GameTypeService,
  ],
  exports: [MatFormFieldModule, MatInputModule],
})
export class AppModule implements DoBootstrap {
  async ngDoBootstrap(app) {
    const keycloakConfig = {
      url: UserData.keycloak_url,
      realm: UserData.keycloak_realm,
      clientId: UserData.keycloak_client,
    };

    try {
      await keycloakService.init({
        config: keycloakConfig,
        initOptions: {
          onLoad: "check-sso",
          checkLoginIframe: false,
        },
      });
      app.bootstrap(AppComponent);
    } catch (error) {
      console.error("Keycloak init failed", error);
    }
  }
}
